import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { ApplicationTaskModel } from '../../../models/borrower-portal/application-task-model';
import { PortalServiceFactory } from '../../../services/portal-service.factory';
import { PortalBaseComponent } from '../../portal-base.component';
import { NotificationService } from 'projects/shared/services/notification.service';
import { getErrorMessageOrDefault } from 'projects/shared/utils/error.utils';
import { FileUploadModel } from "../../../models/tasks/file-upload.model";
import { DirectUploadService, DirectUploadProgress, DirectUploadFileResult } from '../../../services/direct-upload.service';

@Component({
    selector: 'app-add-files-to-task-dialog',
    templateUrl: './add-files-to-task-dialog.component.html',
    styleUrls: ['./add-files-to-task-dialog.component.scss'],
    standalone: false
})
export class AddFilesToTaskDialogComponent extends PortalBaseComponent implements OnInit {

  @Input()
  task: ApplicationTaskModel;

  @Input()
  applicationId: number;

  isUploading = false;
  borrowerNote = '';

  /** True once at least one file reached the task, even if others failed. */
  hasUploadedFiles = false;

  private readonly _destroyRef = inject(DestroyRef);

  constructor(public activeModal: NgbActiveModal,
    private readonly portalServiceFactory: PortalServiceFactory,
    private readonly _notifService: NotificationService,
    private readonly _directUploadService: DirectUploadService
  ) {
    super(portalServiceFactory);
  }

  ngOnInit(): void {
  }

  protected onUploadClicked = (files: FileUploadModel[]): void => {
    this.uploadFiles(files);
  };

  private uploadFiles = (files: FileUploadModel[]) => {
    this.isUploading = true;

    // `files` is the picker's live queue, so files added while uploading land in it too; work
    // from a snapshot of what was actually sent.
    const sentFiles = [...files];

    this.uploadByPassword(sentFiles)
    .pipe(takeUntilDestroyed(this._destroyRef))
    .subscribe({
      next: (results) => {
        this.isUploading = false;

        // Drop the files that made it so a retry only re-sends the ones that didn't
        const uploadedFiles = results
          .filter(r => r.success)
          .map(r => sentFiles[r.fileIndex])
          .filter((f, i, all): f is FileUploadModel => !!f && all.indexOf(f) === i);
        const failedFiles = sentFiles.filter(f => !uploadedFiles.includes(f));
        const remainingFiles = files.filter(f => !uploadedFiles.includes(f));
        files.splice(0, files.length, ...remainingFiles);

        if (uploadedFiles.length > 0) {
          this.hasUploadedFiles = true;
        }

        if (remainingFiles.length === 0) {
          this.activeModal.close();
          return;
        }
        if (failedFiles.length === 0) {
          // Everything sent made it; only files added during the upload are left to send
          return;
        }

        // Keep the dialog open so the borrower sees the failure and can retry. The parent only
        // shows "Upload Complete" when the dialog closes, so a failed upload must not close it.
        failedFiles.forEach(f => {
          f.progress = 0;
        });
        const failureMsg = results
          .filter(r => !r.success)
          .map(r => `${r.fileName}: ${r.error || 'Upload failed'}`)
          .join('; ') || 'Some files were not uploaded.';
        const errorMsg = uploadedFiles.length > 0
          ? `${uploadedFiles.length} of ${sentFiles.length} file(s) uploaded. ${failureMsg}`
          : failureMsg;
        // The toast renders HTML, and file names and server messages are not ours to trust
        this._notifService.showError(_.escape(errorMsg), 'Upload Error');
      },
      error: (err) => {
        const errorMessage = getErrorMessageOrDefault(err, {
          defaultMessage: 'Error uploading documents to task.',
        });
        this._notifService.showError(_.escape(errorMessage), 'Error!');
        sentFiles.forEach(f => {
          f.progress = 0;
        });
        this.isUploading = false;
      }
    });
  }

  // The upload API takes one PDF password per request, so each password-protected file goes up on
  // its own with its password and the rest go together. Results are re-indexed against `sentFiles`.
  private uploadByPassword = (sentFiles: FileUploadModel[]): Observable<DirectUploadFileResult[]> => {
    const unprotectedFiles = sentFiles.filter(f => !f.password);
    const groups = sentFiles.filter(f => !!f.password).map(f => [f]);
    if (unprotectedFiles.length > 0) {
      groups.unshift(unprotectedFiles);
    }

    const uploads$ = groups.map(group => this._directUploadService.uploadToTask(
      this.applicationId,
      group.map(f => f.file),
      this.task.taskId,
      this.borrowerNote?.trim() || undefined,
      (progress: DirectUploadProgress) => {
        const fileModel = this.findGroupFile(group, progress.fileIndex, progress.fileName);
        if (fileModel) {
          fileModel.progress = progress.percent;
        }
      },
      group[0].password
    ).pipe(
      map(results => results.map(r => {
        // -1 when the result matches no file in this request, so it can't claim one from another
        const fileModel = this.findGroupFile(group, r.fileIndex, r.fileName);
        return { ...r, fileIndex: fileModel ? sentFiles.indexOf(fileModel) : -1 };
      })),
      // One request failing must not discard what the others uploaded
      catchError(err => {
        const error = getErrorMessageOrDefault(err, { defaultMessage: 'Error uploading documents to task.' });
        return of(group.map(f => ({
          fileIndex: sentFiles.indexOf(f),
          fileName: f.file.name,
          docFileId: 0,
          fileGuid: '',
          success: false,
          error
        } as DirectUploadFileResult)));
      })
    ));

    return forkJoin(uploads$).pipe(
      map(results => results.flat())
    );
  }

  // Results and progress carry the index of the file as the upload service saw it; the direct
  // upload path pairs prepared files back by name, so fall back to the name if the index doesn't line up.
  private findGroupFile = (group: FileUploadModel[], fileIndex: number, fileName: string): FileUploadModel | undefined => {
    const fileAtIndex = group[fileIndex];
    if (fileAtIndex?.file.name === fileName) {
      return fileAtIndex;
    }
    return group.find(f => f.file.name === fileName);
  }
}
